/*
 * Dazzle House: product videos are always muted, loop, and autoplay
 * silently when they scroll into view. Applies to every product video
 * rendered with data-autoplay-muted (product gallery and lightbox).
 */
(() => {
  if (window.DazzleMutedVideo) return;
  window.DazzleMutedVideo = true;

  const SELECTOR = 'deferred-media[data-autoplay-muted]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inView = new WeakSet();

  const muteVideo = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.loop = true;
    video.setAttribute('loop', '');
  };

  const playVideo = (video) => {
    muteVideo(video);
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  };

  const postToIframe = (iframe, action) => {
    if (!iframe || !iframe.contentWindow) return;
    if (iframe.classList.contains('js-youtube')) {
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' }), '*');
    } else if (iframe.classList.contains('js-vimeo')) {
      iframe.contentWindow.postMessage(JSON.stringify({ method: action }), '*');
    }
  };

  const resume = (el) => {
    const video = el.querySelector('video');
    if (video) return playVideo(video);
    postToIframe(el.querySelector('iframe'), 'play');
  };

  const pause = (el) => {
    const video = el.querySelector('video');
    if (video && !video.paused) video.pause();
    postToIframe(el.querySelector('iframe'), 'pause');
  };

  const init = (DeferredMedia) => {
    const originalLoad = DeferredMedia.prototype.loadContent;

    DeferredMedia.prototype.loadContent = function (focus = true, pauseOthers = true) {
      if (!this.hasAttribute('data-autoplay-muted')) return originalLoad.call(this, focus);
      if (pauseOthers && typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      if (this.getAttribute('loaded')) {
        resume(this);
        return;
      }

      const template = this.querySelector('template');
      if (!template || !template.content.firstElementChild) return;
      const wrapper = document.createElement('div');
      wrapper.appendChild(template.content.firstElementChild.cloneNode(true));
      const media = wrapper.querySelector('video, iframe');
      if (!media) return;
      if (media.nodeName === 'VIDEO') muteVideo(media);

      this.setAttribute('loaded', true);
      const el = this.appendChild(media);
      if (focus) el.focus({ preventScroll: true });
      if (el.nodeName === 'VIDEO') playVideo(el);

      // Safari iframe repaint workaround (same as the theme's original)
      const formerStyle = el.getAttribute('style');
      el.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        if (formerStyle === null) el.removeAttribute('style');
        else el.setAttribute('style', formerStyle);
      }, 0);
    };

    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                  inView.add(target);
                  if (!reduceMotion.matches) target.loadContent(false, false);
                } else {
                  inView.delete(target);
                  pause(target);
                }
              });
            },
            { threshold: 0.25 }
          )
        : null;

    const watch = () => {
      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.dataset.dzWatched) return;
        el.dataset.dzWatched = 'true';
        if (observer) observer.observe(el);
      });
    };

    watch();

    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        watch();
      });
    }).observe(document.body, { childList: true, subtree: true });

    // The theme pauses all media when the lightbox closes; restart visible videos.
    document.body.addEventListener('modalClosed', () => {
      window.setTimeout(() => {
        if (reduceMotion.matches) return;
        document.querySelectorAll(SELECTOR + '[loaded]').forEach((el) => {
          if (inView.has(el)) resume(el);
        });
      }, 150);
    });
  };

  customElements.whenDefined('deferred-media').then(() => init(customElements.get('deferred-media')));
})();
